import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/firebase';

const functions = getFunctions(app);

// Initialize the Stripe Portal callable function
// This is exposed by the Stripe Firebase Extension
const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');

/**
 * Stripe Portal Service
 * 
 * Handles generation of Stripe Customer Portal links for subscription management.
 * The portal allows users to:
 * - Update payment methods
 * - View billing history
 * - Cancel subscriptions
 * - Update billing information
 */

/**
 * Generate a Stripe Customer Portal link and redirect the user
 * 
 * @param {Object} options - Configuration options
 * @param {string} [options.returnUrl] - URL to redirect to after leaving the portal (defaults to current page)
 * @param {boolean} [options.redirect=true] - Whether to automatically redirect (default: true)
 * @returns {Promise<string>} - The portal URL (if redirect is false)
 * 
 * @example
 * // Basic usage - redirects automatically
 * await stripePortalService.openPortal();
 * 
 * @example
 * // Get URL without redirecting
 * const url = await stripePortalService.openPortal({ redirect: false });
 * window.open(url, '_blank');
 * 
 * @example
 * // Custom return URL
 * await stripePortalService.openPortal({ 
 *   returnUrl: 'https://example.com/settings' 
 * });
 */
export const stripePortalService = {
  async openPortal(options = {}) {
    const { returnUrl, redirect = true } = options;

    try {
      // Build the return URL - defaults to current page
      const defaultReturnUrl = returnUrl || window.location.href;
      
      // Call the Stripe Firebase Extension function
      // The function expects a returnUrl parameter
      const result = await createPortalLink({
        returnUrl: defaultReturnUrl,
      });

      // Validate response
      if (!result.data) {
        throw new Error('No data returned from Stripe portal function');
      }

      // Extract the URL from the response
      // The Stripe extension typically returns: { url: 'https://...' }
      const portalUrl = result.data.url || result.data;

      if (!portalUrl || typeof portalUrl !== 'string') {
        throw new Error('Invalid portal URL returned from Stripe');
      }

      // Validate URL format
      try {
        new URL(portalUrl);
      } catch (urlError) {
        throw new Error('Invalid URL format returned from Stripe portal');
      }

      // Redirect if requested (default behavior)
      if (redirect) {
        window.location.href = portalUrl;
        return portalUrl;
      }

      // Return URL if redirect is false
      return portalUrl;
    } catch (error) {
      // Handle Firebase function errors
      if (error.code) {
        switch (error.code) {
          case 'functions/not-found':
            throw new Error(
              'Stripe portal function not found. Please ensure the Stripe Firebase Extension is properly installed.'
            );
          case 'functions/permission-denied':
            throw new Error(
              'Permission denied. Please ensure you are logged in and have an active subscription.'
            );
          case 'functions/unauthenticated':
            throw new Error(
              'You must be logged in to manage your subscription.'
            );
          case 'functions/invalid-argument':
            throw new Error(
              'Invalid request. Please try again or contact support.'
            );
          case 'functions/deadline-exceeded':
            throw new Error(
              'Request timed out. Please try again.'
            );
          case 'functions/resource-exhausted':
            throw new Error(
              'Service temporarily unavailable. Please try again later.'
            );
          case 'functions/failed-precondition':
            throw new Error(
              'Unable to create portal link. Please ensure you have an active subscription.'
            );
          case 'functions/aborted':
            throw new Error(
              'Request was aborted. Please try again.'
            );
          case 'functions/out-of-range':
            throw new Error(
              'Invalid request parameters. Please try again.'
            );
          case 'functions/unimplemented':
            throw new Error(
              'This feature is not available. Please contact support.'
            );
          case 'functions/internal':
            throw new Error(
              'An internal error occurred. Please try again later or contact support.'
            );
          case 'functions/unavailable':
            throw new Error(
              'Service is temporarily unavailable. Please try again later.'
            );
          case 'functions/data-loss':
            throw new Error(
              'Data error occurred. Please try again or contact support.'
            );
          default:
            // For other Firebase function errors, use the message if available
            if (error.message) {
              throw new Error(error.message);
            }
            throw new Error(
              `Failed to create portal link: ${error.code || 'Unknown error'}`
            );
        }
      }

      // Handle network errors
      if (error.message && error.message.includes('network')) {
        throw new Error(
          'Network error. Please check your internet connection and try again.'
        );
      }

      // Handle timeout errors
      if (error.message && (error.message.includes('timeout') || error.message.includes('deadline'))) {
        throw new Error(
          'Request timed out. Please try again.'
        );
      }

      // Handle generic errors
      if (error.message) {
        throw error;
      }

      // Fallback for unknown errors
      throw new Error(
        'An unexpected error occurred while creating the portal link. Please try again or contact support.'
      );
    }
  },

  /**
   * Check if the Stripe portal function is available
   * This is useful for feature detection
   * 
   * @returns {Promise<boolean>} - True if the function is available
   */
  async isAvailable() {
    try {
      // Try to call the function with a test return URL
      // We'll catch the error if it's not available
      await createPortalLink({
        returnUrl: window.location.origin,
      });
      return true;
    } catch (error) {
      // If it's a "not found" error, the function isn't available
      if (error.code === 'functions/not-found') {
        return false;
      }
      // For other errors, assume it's available but there's a different issue
      return true;
    }
  },
};

export default stripePortalService;

